import { useState } from 'react';
import { Building2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CompanyCard } from '../components/CompanyCard';
import { CompaniesFilters } from '../components/CompaniesFilters';
import {
  useCompanies,
  useCreateCompanyFromSiret,
  useUpdateCompany,
  useDeleteCompany,
} from '../hooks/useCompanies';
import type { Company, CompanyFilters, UpdateCompanyDto } from '../types/company.types';

export function CompaniesPage() {
  const [filters, setFilters] = useState<CompanyFilters>({});
  const [page, setPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [siretInput, setSiretInput] = useState('');
  const [editData, setEditData] = useState<UpdateCompanyDto>({});

  const { data, isLoading, error } = useCompanies(filters, { page, limit: 20 }, 'tenants');
  const createFromSiret = useCreateCompanyFromSiret();
  const updateCompany = useUpdateCompany();
  const deleteCompany = useDeleteCompany();

  const handleAddCompany = () => {
    if (!siretInput.trim()) {
      toast.error('Veuillez saisir un numéro SIRET');
      return;
    }

    // Un tenant BPA est par définition un client de la plateforme (abonnement
    // ITC/TM). Le type prospect/client est un concept CRM réservé à /tm/companies.
    createFromSiret.mutate(
      { siret: siretInput.trim(), companyType: 'client', scope: 'tenants' },
      {
        onSuccess: (company) => {
          toast.success(`Tenant BPA "${company.name}" créé avec succès`);
          setShowAddDialog(false);
          setSiretInput('');
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Erreur lors de la création');
        },
      }
    );
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setEditData({
      name: company.name,
      phone: company.phone || undefined,
      email: company.email || undefined,
      website: company.website || undefined,
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = () => {
    if (!selectedCompany) return;

    updateCompany.mutate(
      { id: selectedCompany.id, data: editData },
      {
        onSuccess: () => {
          toast.success('Company mise à jour');
          setShowEditDialog(false);
          setSelectedCompany(null);
        },
        onError: () => {
          toast.error('Erreur lors de la mise à jour');
        },
      }
    );
  };

  const handleDeleteCompany = (company: Company) => {
    setSelectedCompany(company);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!selectedCompany) return;

    deleteCompany.mutate(selectedCompany.id, {
      onSuccess: () => {
        toast.success('Company supprimée');
        setShowDeleteDialog(false);
        setSelectedCompany(null);
      },
      onError: () => {
        toast.error('Erreur lors de la suppression');
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-copper" />
            Tenants BPA
          </h1>
          <p className="text-muted-foreground mt-1">
            Clients SaaS de la plateforme. Les sociétés prospect/client du CRM TM
            sont gérées par chaque tenant dans <span className="font-mono">/tm/companies</span>.
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une entreprise
        </Button>
      </div>

      {/* Filtres */}
      <CompaniesFilters
        filters={filters}
        onFiltersChange={setFilters}
        showTypeFilter={false}
      />

      {/* Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-copper" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-destructive">
          Erreur lors du chargement des entreprises
        </div>
      ) : !data?.data.length ? (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Aucune entreprise trouvée</p>
          <Button
            variant="link"
            className="mt-2"
            onClick={() => setShowAddDialog(true)}
          >
            Ajouter une première entreprise
          </Button>
        </div>
      ) : (
        <>
          <div className="text-sm text-muted-foreground">
            {data.meta.total} entreprise{data.meta.total > 1 ? 's' : ''}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.data.map((company) => (
              <CompanyCard
                key={company.id}
                company={company}
                onEdit={handleEditCompany}
                onDelete={handleDeleteCompany}
              />
            ))}
          </div>

          {/* Pagination */}
          {data.meta.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Précédent
              </Button>
              <span className="flex items-center px-4 text-sm text-muted-foreground">
                Page {page} sur {data.meta.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === data.meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          )}
        </>
      )}

      {/* Dialog Ajouter */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une entreprise</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="siret">Numéro SIRET</Label>
              <Input
                id="siret"
                placeholder="Ex: 12345678901234"
                value={siretInput}
                onChange={(e) => setSiretInput(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Les informations seront automatiquement récupérées depuis l'API
                INSEE. Le tenant est créé comme client de la plateforme BPA.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleAddCompany}
              disabled={createFromSiret.isPending}
            >
              {createFromSiret.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifier */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'entreprise</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nom</Label>
              <Input
                id="edit-name"
                value={editData.name || ''}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Téléphone</Label>
              <Input
                id="edit-phone"
                value={editData.phone || ''}
                onChange={(e) =>
                  setEditData({ ...editData, phone: e.target.value || undefined })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editData.email || ''}
                onChange={(e) =>
                  setEditData({ ...editData, email: e.target.value || undefined })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-website">Site web</Label>
              <Input
                id="edit-website"
                value={editData.website || ''}
                onChange={(e) =>
                  setEditData({ ...editData, website: e.target.value || undefined })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateCompany.isPending}>
              {updateCompany.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Supprimer */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'entreprise ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer "{selectedCompany?.name}" ? Cette
              action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteCompany.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
